import React, { useState } from 'react';
import { List, ListItem, ListItemText, ListSubheader, Collapse, ListItemIcon } from '@material-ui/core';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ExpandMore from '@material-ui/icons/ExpandMore';
import { useHistory, useParams } from 'react-router-dom';
import { DEFAULT_ROUTE, SITE_URLS } from '@constants/routes';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { isEmpty } from 'lodash';
import { LOCAL_STORAGE } from '@constants/common';
import { resetTableState } from '@actions/common.actions';
import { ISideBarItem, internalSideBarItems, publicSideBarItems, ISideBarSubItem } from './sidebarItems';
import { permissionExist } from '../../utils/auth.util';
import useStyles from './styles';

const SideBar: React.FC<ISideBarProps> = (props) => {
  const { account, resetTableState } = props;
  const accountJS = account.toJS();
  const history = useHistory();
  const params: IParams = useParams();
  const isPublicOrg = !!params.organisationGuid;
  const isOrgMgmt = !!params.orgMgmtGuid;
  const isPublicUser = accountJS.isPublicUser;
  const classes = useStyles();

  const isCurrentPath = (path: string) => {
    if (path === SITE_URLS.MAIN) {
      return history.location.pathname === path;
    }
    if (isPublicOrg) {
      return history.location.pathname.includes(path);
    } if (isOrgMgmt) {
      return history.location.pathname.includes(path);
    }
    return history.location.pathname.includes(path);
  };

  const activeSideBarItem = isPublicUser ? publicSideBarItems : internalSideBarItems;

  const getDefaultOpened = (activeSideBarItem) => {
    let defaultOpened = -1;
    activeSideBarItem.forEach((sideBarItem, itemIndex) => {
      !isEmpty(sideBarItem.subItems) && sideBarItem.subItems.forEach(subGroup => {
        subGroup.items.forEach(subItem => {
          if (isCurrentPath(subItem.link)) {
            defaultOpened = itemIndex;
          }
        });
      });
    });

    return defaultOpened;
  };

  const [isOpened, setIsOpened] = useState(getDefaultOpened(activeSideBarItem));

  const constructOrgPath = (path: string) => {
    return `${SITE_URLS.ORG_HOME}/${params.organisationGuid + path}`;
  };

  const constructOrgMgmtPath = (path: string) => {
    return `${SITE_URLS.ORG}/${params.orgMgmtGuid + path}`;
  };

  const handleOpenGroup = (sideBarItem: ISideBarItem, index: number) => {
    let item = null;
    sideBarItem.subItems.forEach(sub => {
      item = sub.items?.find(x => x?.link === DEFAULT_ROUTE[sideBarItem.label]);
    });
    let newIsOpened = index;
    if (isOpened === index) {
      newIsOpened = -1;
    }
    setIsOpened(newIsOpened);
    if(item) {
      permissionExist(account, item.permission) && handleClick(item, false);
    }
  };

  const handleClick = (item: ISideBarSubItem, isSub: boolean = false) => {
    if (item.link) {
      if (!isExactPath(item.link)) {
        resetTableState();
        if (isPublicOrg) {
          history.push(constructOrgPath(item.link));
        } else if (isOrgMgmt && isSub) {
          history.push(constructOrgMgmtPath(item.link));
        } else {
          history.push(item.link);
        }
      }
    }
  };

  const isExactPath = (path: string) => {
    return history.location.pathname === path;
  };

  return (
    <List
      component="div"
      aria-labelledby="side-bar-nested-list"
      className={classes.root}
    >
      { activeSideBarItem.map((sideBarItem, itemIndex) => {
        const itemOpened = isOpened === itemIndex;
        return (
          <React.Fragment key={`${sideBarItem.label}_${itemIndex}`}>
            <ListItem
              component="button"
              button
              key={`${sideBarItem.label}_${itemIndex}`}
              className={`${classes.mainListItem} ${itemOpened && classes.listItemOpened}`}
              onClick={()=> handleOpenGroup(sideBarItem, itemIndex)}
            >
              <ListItemIcon
                className="main-item-icon"
              >
                {sideBarItem.icon}
              </ListItemIcon>
              <ListItemText
                disableTypography
                primary={sideBarItem.label}
              />
              {!isEmpty(sideBarItem.subItems) && (itemOpened ?
                <ExpandMore />
                :
                <ChevronRightIcon />
              )}
            </ListItem>
            {!isEmpty(sideBarItem.subItems) && sideBarItem.subItems.map((subGroup, subGroupIndex) => 
              <Collapse
                key={`${subGroupIndex}_sub_group`}
                in={itemOpened}
                timeout="auto"
                unmountOnExit
                className={classes.subGroup}
              >
                <List
                  component="div"
                  disablePadding
                  className={classes.root}
                  subheader={subGroup.label &&
                    <ListSubheader
                      component="div"
                      id="nested-list-subheader"
                      className={classes.subListHeader}
                    >
                      {subGroup.label}
                    </ListSubheader>}
                >
                  {subGroup.items.map((subItem, subIndex) => 
                    permissionExist(account, subItem.permission) &&
                    <ListItem
                      component="button"
                      button
                      key={`${subItem.label}_${subIndex}`}
                      className={`${classes.subListItem} ${isCurrentPath(subItem.link) && classes.listItemActive}`}
                      onClick={()=> handleClick(subItem, false)}
                    >
                      <ListItemText
                        disableTypography
                        primary={subItem.label}
                      />
                    </ListItem>
                  )}
                </List>
              </Collapse>
            )}
          </React.Fragment>
        );
      })}
    </List>
  );
};

type ISideBarProps = ISideBarStateProps & ISideBarDispatchProps;

interface IParams {
  organisationGuid?: string,
  orgMgmtGuid?: string
}

interface ISideBarStateProps {
  account
}

interface ISideBarDispatchProps {
  resetTableState
}

const mapStateToProps = (state) => ({
  account: state.getIn(['Account', 'accountInfo'])
});

const mapDispatchToProps = (dispatch: any) => ({
  ...bindActionCreators(
    {
      resetTableState,
    },
    dispatch
  )
});


export default connect<ISideBarStateProps, ISideBarDispatchProps>(mapStateToProps, mapDispatchToProps)(SideBar);